import { AdminSignatures } from "@/components/admin/AdminSignatures";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { AdminLoginPage } from "@/components/admin/AdminLoginPage";
import { AdminEmailText } from "@/components/admin/AdminEmailText";
import { GcEmailEdit } from "@/components/EmailAnnualDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { ReportSecPicker } from "@/components/ReportSecPicker";
import { ShowOnlyEditor, ProgressFieldsEditor } from "@/components/admin/AdminReportLists";
import { AdminChurchAccess } from "@/components/admin/AdminChurchAccess";

const FEATURES = [
  ["tithes", "Tithe Reports", "General Council & MWJK tithes"],
  ["progress", "MWJK Progress Report", "Ri-Jar, Ukwelok, Aje and more"],
  ["donations", "Conference Collection", "Offerings, special collections, conference collections"],
  ["conference", "Conference Registration", "Register members for events"],
  ["reports", "Reports", "Church summary, print and export"],
  ["receipts", "Receipt uploads", "Attach receipt screenshots to records"],
  ["messages", "Messages", "Church users can message the Master Admin and receive messages"],
];
const NEW_FEATURES = [
  ["conference_summary", "Conference Summary", "Reports → Conference Summary. Admins need Reports or Finance access. Church access: see Local church — report views.", false],
  ["conference_goals", "Conference Goals", "Set and edit goals (default per church and per church). Admins need Finance or Settings access.", true],
  ["goal_cards", "Conference goal cards on Dashboard", "Goal progress cards on the Dashboard", true],
  ["region_filter", "Region filters", "Region filter on Tithes, Conference Collection and Reports pages", true],
  ["financial_summary", "Financial Summary", "Reports → Financial Summary: Tithes, Conference Collection, Registrations. Admins need Reports or Finance access. Church access: see Local church — report views.", false],
  ["giver_types", "Tithe Type dropdown & filter", "Pastor / Church / Guest(s) / Ritel dropdown, filter and type tables", true],
  ["jemdron", "Jemdron box on Tithe", "Optional Jemdron dropdown under Church in the New Tithe form, and the Jemdron column in the Tithe list and reports", true],
  ["year_comparison", "Year Comparison", "Year Comparison table in Reports", true],
  ["signatures", "Report Signatures", "Prepared by / Concurred by on all reports", true],
  ["export_data", "Export Data", "Admin Panel → Export Data (reports, records, full backup). Admins also need the \"Export Data\" access area in Admin Panel → Users.", true],
];

function FeatureRow({ k, label, help, church, settings, save }) {
  const f = settings.features;
  const on = k === "signatures" ? settings.signatures?.show !== false : f[k] !== false;
  const setOn = (v) => save(k === "signatures" ? { signatures: { show: v } } : { features: { ...f, [k]: v } });
  return (
    <div className="grid grid-cols-[1fr_4.5rem_4.5rem] items-center gap-4 border-b border-slate-100 py-4 last:border-0" data-testid={`feature-row-${k}`}>
      <span><span className="block font-semibold text-slate-900">{label}</span><span className="block text-sm text-slate-500">{help}</span></span>
      <span className="flex justify-center"><OnOffSwitch checked={on} onChange={setOn} testid={`admin-feature-toggle-${k}`} /></span>
      {church ? <span className="flex justify-center"><OnOffSwitch checked={f[`${k}_church`] !== false} onChange={(v) => save({ features: { ...f, [`${k}_church`]: v } })} testid={`admin-feature-church-toggle-${k}`} /></span> : <span className="text-center text-xs text-slate-400">—</span>}
    </div>
  );
}

function NewFeatures({ settings, save }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="new-features-card">
      <h3 className="font-heading text-lg font-bold">Reports & conference features</h3>
      <p className="text-sm text-slate-500">First switch: ON/OFF for everyone. Second switch: church users can see it. Each admin's access follows the areas chosen for them in Admin Panel → Users.</p>
      <div className="mt-2 grid grid-cols-[1fr_4.5rem_4.5rem] gap-4 text-center text-xs font-bold uppercase tracking-wider text-slate-500"><span /><span>Everyone</span><span>Church</span></div>
      <p className="mt-1 text-xs text-slate-500" data-testid="features-legend">Everyone = ON/OFF for all users. Church = whether church users can see it.</p>
      {NEW_FEATURES.map(([k, l, h, c]) => <FeatureRow key={k} k={k} label={l} help={h} church={c} settings={settings} save={save} />)}
    </section>
  );
}
const MESSAGING = [
  ["church_can_send", "Church users can send messages", "When off, church users can only read messages from admins"],
  ["attachments", "Allow file & photo attachments", "Pictures, PDF, Word, Excel, CSV (max 10 MB each)"],
  ["email_church", "Email church users about new messages", "Sends a short \"You have a new message\" email"],
  ["email_admin", "Email admins about new church messages", "Sent to admins who have Messages access"],
];
const ADMIN_MSG = [
  ["admins_in_one", "Show admins in \"One person\"", "Admins with Messages access can be picked as one person to message"],
  ["admins_in_groups", "Show admins in \"Selected churches\"", "Admins appear as choices in the Selected churches list"],
  ["admins_in_all", "Include admins in \"Everyone\"", "Messages to Everyone also go to all admins with Messages access"],
  ["admin_group", "Admin group chat", "One shared conversation where all admins with Messages access can talk together"],
];
const ANNUAL = [
  ["income", "INCOME section", "Income lines, total income, balance forward, grand total income"],
  ["expenditures", "EXPENDITURES section", "All expenditure sections with subtotals and grand total expense"],
  ["balance", "BALANCE section", "Grand total income minus grand total expense"],
  ["totals_by_church", "Annual Totals by Church", "Tithes, conference collections and grand total per church"],
  ["tithes_church_q", "Tithes by Church and Quarter", "Each church's tithes in Q1–Q4"],
  ["tithes_dest_q", "Tithes by Destination and Quarter", "General Council / MWJK tithes in Q1–Q4"],
  ["donations_church_q", "Conference Collection by Church and Quarter", "Each church's conference collection in Q1–Q4"],
  ["donations_category_q", "Conference Collection by Category and Quarter", "Conference Collection categories in Q1–Q4"],
  ["quarterly_totals", "Quarterly Totals (All churches)", "Tithes and conference collections per quarter"],
];
const PERMS = [
  ["church_can_edit", "Church users can edit their records", "Fix mistakes in their own church records"],
  ["church_can_delete", "Church users can delete their records", "When off, only Master Admins can delete"],
  ["church_can_view_reports", "Church users can see Reports", "Summary and Full Detail reports for their own church"],
];
const ADMIN_PERMS = [
  ["admin_can_edit", "Admins can edit records one by one", "Pencil icon on Tithes, Conference Collection, Expenses, Conference Registration and Reports → Full Detail (within each admin's access areas)"],
  ["admin_can_edit_conf_payments", "Admins can edit Conference Summary payments", "Pencil icon on each payment in Reports → Conference Summary"],
  ["admin_can_delete_conf_payments", "Admins can delete Conference Summary payments", "Trash icon on each payment in Reports → Conference Summary"],
];

function ToggleRow({ label, help, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-4 border-b border-slate-100 py-4 last:border-0">
      <span><span className="block font-semibold text-slate-900">{label}</span><span className="block text-sm text-slate-500">{help}</span></span>
      <OnOffSwitch checked={checked} onChange={onChange} testid={testid} />
    </label>
  );
}

function PaymentTypes({ types, save }) {
  const [name, setName] = useState("");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {types.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white py-1 pl-3 pr-1 text-sm font-semibold">
            {t}
            <button onClick={() => save({ payment_types: types.filter((x) => x !== t) })} className="rounded-full p-1 hover:bg-slate-100" data-testid={`payment-type-remove-${t}`}><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
      <form className="flex max-w-sm gap-2" onSubmit={(e) => { e.preventDefault(); if (name.trim()) { save({ payment_types: [...types, name.trim()] }); setName(""); } }}>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add payment type..." className="h-10" data-testid="payment-type-input" />
        <Button type="submit" className="h-10" data-testid="payment-type-add"><Plus className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}

function GcEmail({ value, reload }) {
  return <GcEmailEdit value={value} onSaved={reload} testid="gc-email" />;
}

function AnnualHeading({ annual, save }) {
  const [t, setT] = useState(annual?.title || "");
  const [s, setS] = useState(annual?.subtitle || "");
  return (
    <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); save({ annual: { title: t, subtitle: s } }); }}>
      <Input value={t} onChange={(e) => setT(e.target.value)} maxLength={160} placeholder="Title" className="h-10 bg-white text-slate-900" data-testid="annual-title-input" />
      <Input value={s} onChange={(e) => setS(e.target.value)} maxLength={160} placeholder="Subtitle" className="h-10 bg-white text-slate-900" data-testid="annual-subtitle-input" />
      <p className="text-xs text-slate-500">The year is added after the title automatically.</p>
      <Button type="submit" className="h-10" data-testid="annual-heading-save">Save title</Button>
    </form>
  );
}

function LogoCard({ settings, save }) {
  const { logoUrl, refreshLogo } = useApp();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const b = settings.branding || {};
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/branding/logo", fd);
      await save({ branding: { custom_logo: true, logo_id: r.data.id } });
      refreshLogo();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const toggle = async (v) => {
    if (v && !b.logo_id) { toast.error("Upload a logo first"); return; }
    await save({ branding: { ...b, custom_logo: v } });
    refreshLogo();
  };
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="logo-card">
      <h3 className="font-heading text-lg font-bold">Logo</h3>
      <div className="flex items-center gap-4">
        <img src={logoUrl} alt="Current logo" className="h-20 w-20 shrink-0 rounded-md border border-slate-200 bg-white object-contain p-1" data-testid="logo-preview" />
        <div className="space-y-2 text-sm text-slate-500">
          <p>Shown in the side menu, login page and reports. A square picture (PNG or JPG) looks best.</p>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="logo-file-input" />
          <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="logo-upload-btn">{busy ? "Uploading..." : "Upload new logo"}</Button>
        </div>
      </div>
      <ToggleRow label="Use my uploaded logo" help="OFF shows the original Assemblies of God logo" checked={!!b.custom_logo} testid="logo-custom-toggle" onChange={toggle} />
    </section>
  );
}

export default function AdminSettings() {
  const { settings, reload, isFullAdmin } = useApp();
  const save = async (patch) => {
    try { await api.put("/settings", patch); toast.success("Saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!settings) return null;
  return (
    <div className="grid items-start gap-6 lg:grid-cols-2">
      <div className="lg:col-span-2"><ReportSecPicker /><p className="mt-2 text-xs text-slate-500">Report settings below (Annual Summary title, report sections, Progress columns, Year Comparison, signatures) follow this choice. Other switches are shared.</p></div>
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg font-bold">Show / hide sections</h3>
        <p className="text-sm text-slate-500">Sections turned OFF are hidden from church users. Admins still see them.</p>
        {FEATURES.map(([k, l, h]) => (
          <ToggleRow key={k} label={l} help={h} checked={settings.features[k]} testid={`admin-feature-toggle-${k}`} onChange={(v) => save({ features: { ...settings.features, [k]: v } })} />
        ))}
      </section>
      <NewFeatures settings={settings} save={save} />
      <div className="space-y-6">
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-heading text-lg font-bold">Church user permissions</h3>
          {PERMS.map(([k, l, h]) => (
            <ToggleRow key={k} label={l} help={h} checked={settings.permissions[k]} testid={`admin-perm-toggle-${k}`} onChange={(v) => save({ permissions: { ...settings.permissions, [k]: v } })} />
          ))}
        </section>
        <AdminChurchAccess settings={settings} save={save} />
        {isFullAdmin && (
          <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-edit-perms-card">
            <h3 className="font-heading text-lg font-bold">Admin editing permissions</h3>
            <p className="text-sm text-slate-500">The Master Admin can always edit. Bulk assign to General Council / MWJK is for the Master Admin only.</p>
            {ADMIN_PERMS.map(([k, l, h]) => (
              <ToggleRow key={k} label={l} help={h} checked={settings.permissions[k] !== false} testid={`admin-perm-toggle-${k}`} onChange={(v) => save({ permissions: { ...settings.permissions, [k]: v } })} />
            ))}
          </section>
        )}
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-heading text-lg font-bold">Messages</h3>
          {MESSAGING.map(([k, l, h]) => (
            <ToggleRow key={k} label={l} help={h} checked={settings.messaging?.[k]} testid={`admin-msg-toggle-${k}`} onChange={(v) => save({ messaging: { ...settings.messaging, [k]: v } })} />
          ))}
          {isFullAdmin && ADMIN_MSG.map(([k, l, h]) => (
            <ToggleRow key={k} label={l} help={`${h} (Master Admin only)`} checked={settings.messaging?.[k] !== false} testid={`admin-msg-toggle-${k}`} onChange={(v) => save({ messaging: { ...settings.messaging, [k]: v } })} />
          ))}
        </section>
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-heading text-lg font-bold">Annual Summary Report</h3>
          <ToggleRow label="Annual Summary Report" help="Show the whole report in Reports" checked={settings.features.annual_report} testid="admin-annual-toggle-report" onChange={(v) => save({ features: { ...settings.features, annual_report: v } })} />
          {ANNUAL.map(([k, l, h]) => (
            <ToggleRow key={k} label={l} help={h} checked={settings.annual_sections?.[k]} testid={`admin-annual-toggle-${k}`} onChange={(v) => save({ annual_sections: { ...settings.annual_sections, [k]: v } })} />
          ))}
        </section>
        <div className="space-y-2" data-testid="report-lists-side">
          <ReportSecPicker testid="report-lists-sec-picker" />
          <p className="text-xs text-white/80">Pick General Council or MWJK here to edit that side's "Show only" choices and Progress Report columns.</p>
        </div>
        <ShowOnlyEditor key={`only-${settings.report_sec}`} settings={settings} save={save} />
        <ProgressFieldsEditor key={`pf-${settings.report_sec}`} settings={settings} save={save} />
        <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="annual-heading-card">
          <h3 className="font-heading text-lg font-bold">Annual Summary title</h3>
          <AnnualHeading key={settings.report_sec} annual={settings.annual} save={save} />
        </section>
        <LogoCard settings={settings} save={save} />
        <AdminSignatures save={save} />
        <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="gc-email-card">
          <h3 className="font-heading text-lg font-bold">General Council office email</h3>
          <p className="text-sm text-slate-500">The Annual Summary Report is emailed to this address from Reports → Annual Summary.</p>
          <GcEmail value={settings.annual?.gc_email} reload={reload} />
        </section>
        <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-heading text-lg font-bold">Payment types</h3>
          <PaymentTypes types={settings.payment_types} save={save} />
        </section>
      </div>
      {isFullAdmin && <AdminLoginPage />}
      {isFullAdmin && <AdminEmailText />}
    </div>
  );
}
